import type { NodeExecution } from '@/types/monitoring';
import type { AgentToolActionInfo } from '@/services/monitoringApi';

/**
 * Match the runtime constant in `useMonitoringData` (`NODE_ID_PREFIX_CHATMSG_ACTION`).
 * Duplicated here so the resolver has no runtime dependency on that hook module.
 */
export const CHATMSG_ACTION_PREFIX = 'chatmsg-action-';
export const TOOL_NODE_PREFIX = 'tool-';

export interface ActionDetails {
  input?: unknown;
  output?: unknown;
  agentMetadata?: NodeExecution['agentMetadata'];
  retryHistory?: NodeExecution['retryHistory'];
  requestContent?: NodeExecution['requestContent'];
  canResubmit?: boolean | undefined;
}

export interface SelectedNodeExecutionContext {
  nodeExecutions: NodeExecution[];
  actionDetails: ActionDetails | null | undefined;
  chatMessageIterationActions: Map<number, NodeExecution[]> | null | undefined;
  toolActions: Map<string, AgentToolActionInfo[]>;
  selectedToolIteration: number | null;
  scopeActions: Map<string, NodeExecution[]>;
  selectedScopeRepetitions: Record<string, string>;
}

/**
 * Chat-message actions are identified by the "chatmsg-action-{iterIdx}-{name}"
 * prefix. Look up status in the per-iteration action map and enrich with
 * input/output from actionDetails if available.
 */
export function resolveChatMessageNodeExecution(
  nodeId: string,
  ctx: Pick<SelectedNodeExecutionContext, 'chatMessageIterationActions' | 'actionDetails'>
): NodeExecution {
  const withoutPrefix = nodeId.slice(CHATMSG_ACTION_PREFIX.length);
  const firstDashIndex = withoutPrefix.indexOf('-');
  const iterationIndexStr = firstDashIndex >= 0 ? withoutPrefix.slice(0, firstDashIndex) : '';
  const actionName = firstDashIndex >= 0 ? withoutPrefix.slice(firstDashIndex + 1) : withoutPrefix;
  const iterationIndex = parseInt(iterationIndexStr, 10);

  let status: NodeExecution['status'] = 'succeeded';
  if (!Number.isNaN(iterationIndex) && ctx.chatMessageIterationActions) {
    const iterationActions = ctx.chatMessageIterationActions.get(iterationIndex);
    const matching = iterationActions?.find((a) => a.nodeName === actionName);
    if (matching) {
      status = matching.status;
    }
  }

  return {
    nodeId,
    nodeName: actionName,
    nodeType: 'Action',
    status,
    input: ctx.actionDetails?.input,
    output: ctx.actionDetails?.output,
  };
}

/**
 * Tool nodes are "tool-{agentName}-{toolName}". Look up execution data in
 * `toolActions`, preferring the currently selected iteration when set, then
 * falling back to any iteration for that agent. Falls back to a bare shell
 * for built-in tools whose iteration hasn't been expanded yet.
 */
export function resolveToolNodeExecution(
  nodeId: string,
  ctx: Pick<SelectedNodeExecutionContext, 'toolActions' | 'selectedToolIteration'>
): NodeExecution | null {
  const withoutPrefix = nodeId.slice(TOOL_NODE_PREFIX.length);
  const firstDashIndex = withoutPrefix.indexOf('-');
  if (firstDashIndex < 0) return null;

  const agentName = withoutPrefix.slice(0, firstDashIndex);
  const toolName = withoutPrefix.slice(firstDashIndex + 1);

  const iterationName = ctx.selectedToolIteration !== null
    ? String(ctx.selectedToolIteration).padStart(6, '0')
    : null;
  const preferredKey = iterationName ? `${agentName}-${iterationName}` : null;

  const otherKeys = Array.from(ctx.toolActions.keys()).filter(
    (k) => k !== preferredKey && k.startsWith(agentName + '-')
  );
  const keysToSearch = preferredKey ? [preferredKey, ...otherKeys] : otherKeys;

  for (const iterationKey of keysToSearch) {
    const actions = ctx.toolActions.get(iterationKey);
    if (!actions) continue;
    const matching = actions.find((a) => a.toolName === toolName || a.actionName === toolName);
    if (matching) {
      return {
        nodeId,
        nodeName: matching.actionName || toolName,
        nodeType: 'Tool',
        status: matching.status,
        input: matching.input,
        output: matching.output,
        startTime: matching.startTime,
        endTime: matching.endTime,
        duration: matching.duration,
        error: matching.error,
      };
    }
  }

  // Built-in tools (e.g. code_interpreter) may have no toolActions entry yet.
  return {
    nodeId,
    nodeName: toolName,
    nodeType: 'Tool',
    status: 'succeeded',
  };
}

/**
 * Generic node lookup for triggers, actions, and scope children:
 *
 * 1. Exact match by `nodeId` in top-level executions
 * 2. Fallback by trailing name (canvas branch scoping like
 *    "action-IsApproved/Compose_1" vs "action-Compose_1")
 * 3. Selected repetition scope actions, then any scope actions
 * 4. Enrich with actionDetails input/output/agentMetadata/retryHistory
 *    (and an explicit canResubmit) when present, without overriding
 *    non-empty values on the base execution.
 */
export function resolveGenericNodeExecution(
  nodeId: string,
  ctx: Pick<
    SelectedNodeExecutionContext,
    'nodeExecutions' | 'actionDetails' | 'scopeActions' | 'selectedScopeRepetitions'
  >
): NodeExecution | null {
  const { nodeExecutions, actionDetails, scopeActions, selectedScopeRepetitions } = ctx;
  if (nodeExecutions.length === 0 && scopeActions.size === 0) return null;

  const nodeNameIndex = new Map<string, NodeExecution>();
  for (const exec of nodeExecutions) {
    if (!nodeNameIndex.has(exec.nodeName)) {
      nodeNameIndex.set(exec.nodeName, exec);
    }
  }

  const nodeExec = nodeExecutions.find((n) => n.nodeId === nodeId);

  if (!nodeExec) {
    const actionName = nodeId.replace(/^(trigger-|action-)/, '').split('/').pop();
    if (actionName) {
      const fallback = nodeNameIndex.get(actionName);
      if (fallback) return fallback;
    }

    for (const [scopeName, repetitionName] of Object.entries(selectedScopeRepetitions)) {
      const actionKey = `${scopeName}-${repetitionName}`;
      const actions = scopeActions.get(actionKey);
      const match = actions?.find((a) => a.nodeId === nodeId);
      if (match) return match;
    }
    for (const actions of scopeActions.values()) {
      const match = actions.find((a) => a.nodeId === nodeId);
      if (match) return match;
    }
    return null;
  }

  if (actionDetails) {
    if (actionDetails.requestContent) {
      return {
        ...nodeExec,
        requestContent: actionDetails.requestContent,
        input: actionDetails.requestContent.input.status === 'available' ? actionDetails.requestContent.input.data : undefined,
        output: actionDetails.requestContent.output.status === 'available' ? actionDetails.requestContent.output.data : undefined,
      };
    }
    return {
      ...nodeExec,
      input: actionDetails.input ?? nodeExec.input,
      output: actionDetails.output ?? nodeExec.output,
      agentMetadata: actionDetails.agentMetadata ?? nodeExec.agentMetadata,
      retryHistory: actionDetails.retryHistory ?? nodeExec.retryHistory,
      ...(typeof actionDetails.canResubmit === 'boolean' ? { canResubmit: actionDetails.canResubmit } : {}),
    };
  }

  return nodeExec;
}

/**
 * Dispatcher: pick the right resolver based on the node id prefix.
 *
 * Returns `null` when no node is selected or nothing matches — the logs
 * panel treats null as "hide details".
 */
export function resolveSelectedNodeExecution(
  selectedNodeId: string | null | undefined,
  ctx: SelectedNodeExecutionContext
): NodeExecution | null {
  if (!selectedNodeId) return null;

  if (selectedNodeId.startsWith(CHATMSG_ACTION_PREFIX)) {
    return resolveChatMessageNodeExecution(selectedNodeId, ctx);
  }

  if (selectedNodeId.startsWith(TOOL_NODE_PREFIX)) {
    return resolveToolNodeExecution(selectedNodeId, ctx);
  }

  return resolveGenericNodeExecution(selectedNodeId, ctx);
}
